// A folha imprimível da lista de concursos — o que o app de origem faz com
// jsPDF + jspdf-autotable.
//
// Puro: entra a lista, sai uma string de HTML. Quem abre a janela e chama o
// `print()` é a tela, porque isso é IO. Mesma divisão de `resumoHonorarios.ts`.
//
// ---------------------------------------------------------------------------
// Por que impressão do navegador, e não a biblioteca da origem
// ---------------------------------------------------------------------------
// O PDF da origem é TEXTO vetorial (jsPDF + autoTable), não imagem — então ele
// não tem o defeito que o Gerador de Currículos teve, onde html2canvas produzia
// fatias JPEG que o ATS não lia. O motivo de não portar a biblioteca é outro:
// o hub já imprime pelo navegador em três lugares (o resumo de honorários, o
// recibo do DP, o currículo), e uma segunda maneira de gerar papel é uma segunda
// maneira de ele sair diferente. Duas dependências a menos, também.
//
// ---------------------------------------------------------------------------
// Por que cada valor passa por `escapar`
// ---------------------------------------------------------------------------
// O órgão, o cargo e a banca são digitados pela equipe na tela de admin. Este
// HTML é escrito com `document.write` numa janela mesma-origem do hub, então um
// `<script>` num nome de órgão rodaria com a sessão de quem imprime. Não existe
// interpolação crua aqui.
function escapar(v: string | null | undefined): string {
  return String(v ?? "")
    .split("&")
    .join("&amp;")
    .split("<")
    .join("&lt;")
    .split(">")
    .join("&gt;")
    .split('"')
    .join("&quot;")
    .split("'")
    .join("&#39;");
}

export type LinhaDaFolha = {
  orgao: string;
  cargo: string;
  uf: string;
  cidade: string | null;
  banca: string | null;
  situacao: string;
  inscricaoTexto: string | null;
  dataProva: string | null;
  remuneracao: number | null;
  conferidoEm: string | null;
};

const DATA = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const [a, m, d] = String(iso).split("-");
  return a && m && d ? `${d}/${m}/${a}` : "—";
};

const REAIS = (v: number | null): string =>
  v === null ? "—" : v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/**
 * A folha, em paisagem, com o que a origem imprime — mais a coluna que ela não
 * tem.
 *
 * A origem imprime 7 colunas: Órgão, Cargo, UF, Status, Banca, Data Prova,
 * Remuneração. Aqui entram as mesmas e mais **Conferido em**, porque o dado é
 * perecível e uma folha impressa sobrevive ao dado: sem a data ao lado, a lista
 * de amanhã e a de três meses atrás são indistinguíveis na mesa de alguém.
 *
 * `titulo` diz qual filtro gerou a folha — a origem imprime a lista FILTRADA e
 * não diz qual filtro era.
 */
export function htmlDaFolha(
  linhas: LinhaDaFolha[],
  cab: { titulo: string; geradoEm: string; geradoPor: string | null },
): string {
  const corpo = linhas
    .map(
      (l) => `<tr>
      <td>${escapar(l.orgao)}</td>
      <td>${escapar(l.cargo)}</td>
      <td class="c">${escapar(l.uf)}</td>
      <td>${escapar(l.cidade ?? "—")}</td>
      <td>${escapar(l.situacao)}</td>
      <td>${escapar(l.inscricaoTexto ?? "—")}</td>
      <td>${escapar(l.banca ?? "—")}</td>
      <td class="c">${escapar(DATA(l.dataProva))}</td>
      <td class="n">${escapar(REAIS(l.remuneracao))}</td>
      <td class="c">${escapar(DATA(l.conferidoEm))}</td>
    </tr>`,
    )
    .join("\n");

  return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<title>${escapar(cab.titulo)}</title>
<style>
  @page { size: A4 landscape; margin: 12mm; }
  /* Cor FIXA, e isto não é exceção à doutrina — é a doutrina: papel não troca de
     tema. Cor semântica aqui faria a folha mudar de aparência conforme o tema de
     quem imprime, o que é absurdo. */
  body { font: 10px/1.35 -apple-system, "Segoe UI", Roboto, sans-serif; color: #14181f; }
  h1 { font-size: 15px; margin: 0 0 2px; color: #102850; }
  .sub { font-size: 10px; color: #5b6472; margin: 0 0 10px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 4px 6px; border-bottom: 1px solid #d9dde4; vertical-align: top; }
  th { background: #102850; color: #fff; text-align: left; font-size: 9px;
       text-transform: uppercase; letter-spacing: .04em; }
  td.c { text-align: center; white-space: nowrap; }
  td.n { text-align: right; white-space: nowrap; }
  tbody tr:nth-child(even) td { background: #f6f8fb; }
  thead { display: table-header-group; }
  tr { break-inside: avoid; }
  footer { margin-top: 10px; font-size: 9px; color: #5b6472; }
</style>
</head>
<body>
<h1>${escapar(cab.titulo)}</h1>
<p class="sub">${escapar(String(linhas.length))} concurso(s) · gerado em ${escapar(cab.geradoEm)}${
    cab.geradoPor ? ` por ${escapar(cab.geradoPor)}` : ""
  }</p>
<table>
<thead><tr>
  <th>Órgão</th><th>Cargo</th><th>UF</th><th>Cidade</th><th>Situação</th>
  <th>Inscrições</th><th>Banca</th><th>Prova</th><th>Remuneração</th><th>Conferido</th>
</tr></thead>
<tbody>
${corpo}
</tbody>
</table>
<footer>
  Mapa de Concursos Contábeis — Contabilidade Facilitada. As datas são o que a
  equipe leu no edital na data de conferência de cada linha; o edital é a fonte
  oficial.
</footer>
</body>
</html>`;
}
